import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  queryCollection, 
  createDocument, 
  updateDocument, 
  deleteDocument,
  batchQuery 
} from '../lib/api';
import { 
  CurrencyCircleDollar, 
  TrendUp, 
  TrendDown,
  Plus,
  PencilSimple,
  Trash,
  CalendarBlank,
  FunnelSimple,
  MagnifyingGlass,
  CaretDown,
  CaretUp,
  Export,
  X,
  Check,
  House,
  ArrowsClockwise
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  EXPENSE_CATEGORIES, 
  INCOME_TYPES,
  FinanceEntry 
} from '../components/economy';
import { IncomeDialog } from '../components/economy/IncomeDialog';
import { ExpenseDialog } from '../components/economy/ExpenseDialog';

// Frequency options
const FREQUENCY_OPTIONS = [
  { id: 'once', label: 'Engångs' },
  { id: 'monthly', label: 'Månadsvis' },
  { id: 'quarterly', label: 'Kvartalsvis' },
  { id: 'yearly', label: 'Årligen' }
];

// Types
interface Income {
  id: string;
  apartment_id?: string;
  type: string;
  amount: number | string;
  date: string;
  description?: string;
}

interface Expense {
  id: string;
  category: string;
  amount: number | string;
  date: string;
  description?: string;
  is_recurring?: boolean;
  frequency?: string;
  vendor?: string;
}

interface Apartment {
  id: string;
  apartment_number?: string;
  name?: string;
}

interface Property {
  id: string;
  name?: string;
}

export default function EconomyPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  
  // Filters
  const [dateFilter, setDateFilter] = useState('year');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState<Income | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  
  // Forms
  const [incomeForm, setIncomeForm] = useState({
    apartment_id: '',
    type: 'rent',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  
  const [expenseForm, setExpenseForm] = useState({
    category: 'other',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    is_recurring: false,
    frequency: 'once',
    vendor: ''
  });

  const fetchData = useCallback(async () => {
    if (!user?.organization_id) return;
    
    setLoading(true);
    try {
      const queries = [
        { collection: 'income_records', filter: { organization_id: user.organization_id }, limit: 1000 },
        { collection: 'expense_records', filter: { organization_id: user.organization_id }, limit: 1000 },
        { collection: 'apartments', filter: { organization_id: user.organization_id }, limit: 500 },
        { collection: 'properties', filter: {}, limit: 100 }
      ];
      
      const results = await batchQuery(queries);
      
      setIncomes((results.income_records || []) as Income[]);
      setExpenses((results.expense_records || []) as Expense[]);
      setApartments((results.apartments || []) as Apartment[]);
      setProperties((results.properties || []) as Property[]);
    } catch (error) {
      console.error('Failed to fetch economy data:', error);
      toast.error('Kunde inte hämta ekonomidata');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter data by date range
  const getDateRange = () => {
    const now = new Date();
    let start;
    
    switch (dateFilter) {
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
      default:
        start = new Date(2000, 0, 1);
    }
    
    return { start, end: now };
  };

  // Filtered and calculated data
  const filteredData = useMemo(() => {
    const { start, end } = getDateRange();
    
    const filteredIncomes = incomes.filter(i => {
      const date = new Date(i.date);
      return date >= start && date <= end;
    });
    
    const filteredExpenses = expenses.filter(e => {
      const date = new Date(e.date);
      const matchesDate = date >= start && date <= end;
      const matchesCategory = categoryFilter === 'all' || e.category === categoryFilter;
      const matchesSearch = !searchQuery || 
        e.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.vendor?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesDate && matchesCategory && matchesSearch;
    });
    
    const totalIncome = filteredIncomes.reduce((sum, i) => sum + (typeof i.amount === 'string' ? parseFloat(i.amount) : i.amount || 0), 0);
    const totalExpenses = filteredExpenses.reduce((sum, e) => sum + (typeof e.amount === 'string' ? parseFloat(e.amount) : e.amount || 0), 0);
    const balance = totalIncome - totalExpenses;
    
    // Group expenses by category
    const expensesByCategory = EXPENSE_CATEGORIES.map(cat => ({
      ...cat,
      total: filteredExpenses
        .filter(e => e.category === cat.id)
        .reduce((sum, e) => sum + (typeof e.amount === 'string' ? parseFloat(e.amount) : e.amount || 0), 0),
      count: filteredExpenses.filter(e => e.category === cat.id).length
    })).filter(cat => cat.total > 0);
    
    // Group incomes by apartment
    const incomesByApartment = apartments.map(apt => ({
      apartment: apt,
      total: filteredIncomes
        .filter(i => i.apartment_id === apt.id)
        .reduce((sum, i) => sum + (typeof i.amount === 'string' ? parseFloat(i.amount) : i.amount || 0), 0),
      records: filteredIncomes.filter(i => i.apartment_id === apt.id)
    })).filter(a => a.total > 0);
    
    return {
      incomes: filteredIncomes,
      expenses: filteredExpenses,
      totalIncome,
      totalExpenses,
      balance,
      expensesByCategory,
      incomesByApartment
    };
  }, [incomes, expenses, apartments, dateFilter, categoryFilter, searchQuery]);

  // Handle income submit
  const handleIncomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.organization_id) return;
    
    try {
      const data = {
        ...incomeForm,
        amount: parseFloat(incomeForm.amount),
        organization_id: user.organization_id,
        created_at: new Date().toISOString()
      };
      
      if (selectedIncome) {
        await updateDocument('income_records', selectedIncome.id, data);
        toast.success('Intäkt uppdaterad');
      } else {
        await createDocument('income_records', data);
        toast.success('Intäkt tillagd');
      }
      
      setShowIncomeDialog(false);
      resetIncomeForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save income:', error);
      toast.error('Kunde inte spara intäkt');
    }
  };

  // Handle expense submit
  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.organization_id) return;
    
    try {
      const data = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        organization_id: user.organization_id,
        created_at: new Date().toISOString()
      };
      
      if (selectedExpense) {
        await updateDocument('expense_records', selectedExpense.id, data);
        toast.success('Utgift uppdaterad');
      } else {
        await createDocument('expense_records', data);
        toast.success('Utgift tillagd');
      }
      
      setShowExpenseDialog(false);
      resetExpenseForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save expense:', error);
      toast.error('Kunde inte spara utgift');
    }
  };

  // Delete handlers
  const handleDeleteIncome = async (id: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna intäkt?')) return;
    
    try {
      await deleteDocument('income_records', id);
      toast.success('Intäkt borttagen');
      fetchData();
    } catch (error) {
      console.error('Failed to delete income:', error);
      toast.error('Kunde inte ta bort intäkt');
    }
  };

  const handleDeleteExpense = async (id: string) => {
    if (!confirm('Är du säker på att du vill ta bort denna utgift?')) return;
    
    try {
      await deleteDocument('expense_records', id);
      toast.success('Utgift borttagen');
      fetchData();
    } catch (error) {
      console.error('Failed to delete expense:', error);
      toast.error('Kunde inte ta bort utgift');
    }
  };

  // Edit handlers
  const handleEditIncome = (income: Income) => {
    setSelectedIncome(income);
    setIncomeForm({
      apartment_id: income.apartment_id || '',
      type: income.type || 'rent',
      amount: income.amount?.toString() || '',
      date: income.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      description: income.description || ''
    });
    setShowIncomeDialog(true);
  };

  const handleEditExpense = (expense: Expense) => {
    setSelectedExpense(expense);
    setExpenseForm({
      category: expense.category || 'other',
      amount: expense.amount?.toString() || '',
      date: expense.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      description: expense.description || '',
      is_recurring: expense.is_recurring || false,
      frequency: expense.frequency || 'once',
      vendor: expense.vendor || ''
    });
    setShowExpenseDialog(true);
  };

  // Reset forms
  const resetIncomeForm = () => {
    setSelectedIncome(null);
    setIncomeForm({
      apartment_id: '',
      type: 'rent',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: ''
    });
  };

  const resetExpenseForm = () => {
    setSelectedExpense(null);
    setExpenseForm({
      category: 'other',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
      is_recurring: false,
      frequency: 'once',
      vendor: ''
    });
  };

  // Format currency
  const formatCurrency = (amount: number | string | undefined) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Get apartment display name
  const getApartmentName = (apartmentId: string | undefined) => {
    if (!apartmentId) return 'Okänd';
    const apt = apartments.find(a => a.id === apartmentId);
    if (!apt) return 'Okänd';
    return apt.apartment_number || apt.name || 'Lägenhet';
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Typ', 'Datum', 'Beskrivning', 'Belopp', 'Kategori'];
    const rows = [
      ...filteredData.incomes.map(i => [
        'Intäkt',
        i.date,
        i.description || getApartmentName(i.apartment_id),
        i.amount,
        INCOME_TYPES.find(t => t.id === i.type)?.label || i.type
      ]),
      ...filteredData.expenses.map(e => [
        'Utgift',
        e.date,
        e.description || e.vendor,
        -e.amount,
        EXPENSE_CATEGORIES.find(c => c.id === e.category)?.label || e.category
      ])
    ];
    
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ekonomi_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
        <div className="grid md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="economy-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ekonomi</h1>
          <p className="text-slate-500">Översikt över intäkter och utgifter</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportToCSV}>
            <Export size={18} className="mr-2" />
            Exportera
          </Button>
          <Button 
            variant="outline"
            onClick={() => { resetIncomeForm(); setShowIncomeDialog(true); }}
          >
            <TrendUp size={18} className="mr-2" />
            Ny intäkt
          </Button>
          <Button 
            onClick={() => { resetExpenseForm(); setShowExpenseDialog(true); }}
          >
            <Plus size={18} className="mr-2" />
            Ny utgift
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-40">
            <CalendarBlank size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Denna månad</SelectItem>
            <SelectItem value="quarter">Detta kvartal</SelectItem>
            <SelectItem value="year">Detta år</SelectItem>
            <SelectItem value="all">All tid</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <FunnelSimple size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla kategorier</SelectItem>
            {EXPENSE_CATEGORIES.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Sök utgifter..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Intäkter</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(filteredData.totalIncome)}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendUp size={28} />
            </div>
          </div>
          <p className="text-green-100 text-sm mt-4">
            {filteredData.incomes.length} transaktioner
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm font-medium">Utgifter</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(filteredData.totalExpenses)}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <TrendDown size={28} />
            </div>
          </div>
          <p className="text-red-100 text-sm mt-4">
            {filteredData.expenses.length} transaktioner
          </p>
        </div>
        
        <div className={`bg-gradient-to-br ${
          filteredData.balance >= 0 
            ? 'from-blue-500 to-blue-600' 
            : 'from-amber-500 to-amber-600'
        } rounded-xl p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm font-medium">Resultat</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(filteredData.balance)}</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <CurrencyCircleDollar size={28} />
            </div>
          </div>
          <p className="text-white/80 text-sm mt-4">
            {filteredData.balance >= 0 ? 'Överskott' : 'Underskott'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { id: 'overview', label: 'Översikt' },
          { id: 'incomes', label: 'Intäkter' },
          { id: 'expenses', label: 'Utgifter' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Expenses by Category */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Utgifter per kategori</h3>
            {filteredData.expensesByCategory.length === 0 ? (
              <p className="text-slate-500 text-sm">Inga utgifter för vald period</p>
            ) : (
              <div className="space-y-3">
                {filteredData.expensesByCategory.map(cat => {
                  const Icon = cat.icon;
                  const percentage = (cat.total / filteredData.totalExpenses) * 100;
                  return (
                    <div key={cat.id} className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cat.color}`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-900">{cat.label}</span>
                          <span className="text-sm font-semibold text-slate-900">
                            {formatCurrency(cat.total)}
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-slate-400 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Top Apartments by Income */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Intäkter per lägenhet</h3>
            {filteredData.incomesByApartment.length === 0 ? (
              <p className="text-slate-500 text-sm">Inga intäkter för vald period</p>
            ) : (
              <div className="space-y-3">
                {filteredData.incomesByApartment.slice(0, 8).map(item => (
                  <div key={item.apartment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                        <House size={20} />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">
                          Lgh {item.apartment.apartment_number || item.apartment.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {item.records.length} betalningar
                        </div>
                      </div>
                    </div>
                    <span className="font-semibold text-green-600">
                      {formatCurrency(item.total)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'incomes' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Datum</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Lägenhet</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Typ</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Beskrivning</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Belopp</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Åtgärd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.incomes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      Inga intäkter registrerade
                    </td>
                  </tr>
                ) : (
                  filteredData.incomes
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map(income => (
                      <tr key={income.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-900">
                          {new Date(income.date).toLocaleDateString('sv-SE')}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-900">
                          {getApartmentName(income.apartment_id)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {INCOME_TYPES.find(t => t.id === income.type)?.label || income.type}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {income.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right">
                          +{formatCurrency(income.amount)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEditIncome(income)}
                            >
                              <PencilSimple size={16} />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteIncome(income.id)}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Datum</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Kategori</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Beskrivning</th>
                  <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Leverantör</th>
                  <th className="text-center text-xs font-medium text-slate-500 uppercase px-4 py-3">Typ</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Belopp</th>
                  <th className="text-right text-xs font-medium text-slate-500 uppercase px-4 py-3">Åtgärd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredData.expenses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      Inga utgifter registrerade
                    </td>
                  </tr>
                ) : (
                  filteredData.expenses
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map(expense => {
                      const category = EXPENSE_CATEGORIES.find(c => c.id === expense.category);
                      const Icon = category?.icon || CurrencyCircleDollar;
                      return (
                        <tr key={expense.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm text-slate-900">
                            {new Date(expense.date).toLocaleDateString('sv-SE')}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${category?.color || 'bg-slate-100 text-slate-600'}`}>
                                <Icon size={16} />
                              </div>
                              <span className="text-sm text-slate-900">{category?.label || expense.category}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {expense.description || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {expense.vendor || '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {expense.is_recurring ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                <ArrowsClockwise size={12} />
                                {FREQUENCY_OPTIONS.find(f => f.id === expense.frequency)?.label || 'Återkommande'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                                <CalendarBlank size={12} />
                                Engångs
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-red-600 text-right">
                            -{formatCurrency(expense.amount)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditExpense(expense)}
                              >
                                <PencilSimple size={16} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteExpense(expense.id)}
                              >
                                <Trash size={16} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Income Dialog */}
      <IncomeDialog
        open={showIncomeDialog}
        onOpenChange={setShowIncomeDialog}
        selectedIncome={selectedIncome as FinanceEntry | null}
        form={incomeForm}
        onFormChange={setIncomeForm}
        onSubmit={handleIncomeSubmit}
        apartments={apartments}
      />

      {/* Expense Dialog */}
      <ExpenseDialog
        open={showExpenseDialog}
        onOpenChange={setShowExpenseDialog}
        selectedExpense={selectedExpense as FinanceEntry | null}
        form={expenseForm}
        onFormChange={setExpenseForm}
        onSubmit={handleExpenseSubmit}
      />
    </div>
  );
}
